import React, { useEffect, useMemo, useState } from "react";
import { DeliveryMap } from "./DeliveryMap";
import { productPickerLabel, productToOrderLineSku, productVariantSummary } from "../utils/productDisplay";
import { orderItemThumbnailSrc } from "../utils/orderLineMedia";
import { formatMoney } from "../utils/money";
import { friendlyError } from "../utils/friendlyError";
import { getMerchantProfile } from "../services/api";
import { t } from "../i18n";

function emptyLine() {
  return {
    productId: null,
    quantity: 1,
    _selected: false,
  };
}

function Section({ step, title, hint, error, children }) {
  return (
    <section className={`order-section${error ? " order-section--error" : ""}`}>
      <div className="order-section__head">
        <span className="order-section__step">{step}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="order-section__title">{title}</div>
          {hint ? <div className="order-section__hint">{hint}</div> : null}
        </div>
        {error ? <span className="order-section__flag">{t("form.required")}</span> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * New order form (catalog lines → receiver → delivery pin). Rendered inside the New order panel on Orders;
 * the footer with the running total and submit button stays pinned at the bottom of the panel.
 */
export function FulfillmentForm({ merchantId, products = [], onSubmit, onCancel }) {
  const [items, setItems] = useState([emptyLine()]);
  const [delivery, setDelivery] = useState({});
  // Receiver phone: user enters 9 digits; we store local digits only.
  const [clientPhone, setClientPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  // Who pays the driver's delivery fee for this order; starts at the store's default.
  const [feePayer, setFeePayer] = useState("SENDER");

  useEffect(() => {
    if (!merchantId) return;
    getMerchantProfile(merchantId)
      .then((p) => {
        if (p?.defaultDeliveryFeePayer) setFeePayer(p.defaultDeliveryFeePayer);
      })
      .catch(() => {});
  }, [merchantId]);

  const payload = useMemo(() => {
    const digits = clientPhone.replace(/\D/g, "");
    return {
      merchantId,
      items: items
        .filter((i) => i.productId != null && i.quantity >= 1)
        .map((i) => {
          const p = products.find((x) => x.id === i.productId);
          return {
            sku: productToOrderLineSku(p),
            quantity: Number(i.quantity),
            unitPrice: String(p.unitPrice),
          };
        }),
      // Always send canonical digits form (2519XXXXXXXX) to match odos_client enforcement.
      clientPhone: digits ? `251${digits}` : undefined,
      deliveryAddress: delivery.deliveryAddress || undefined,
      deliveryLatitude: delivery.deliveryLatitude,
      deliveryLongitude: delivery.deliveryLongitude,
      deliveryFeePayer: feePayer,
    };
  }, [merchantId, products, items, delivery, clientPhone, feePayer]);

  const totals = useMemo(() => {
    let count = 0;
    let sum = 0;
    for (const i of items) {
      const p = products.find((x) => x.id === i.productId);
      const q = Number(i.quantity) || 0;
      if (!p || q < 1) continue;
      count += q;
      sum += q * (Number(p.unitPrice) || 0);
    }
    return { count, sum };
  }, [items, products]);

  function validateForm() {
    const errors = {};

    // Check if at least one item is selected
    if (!items.some((i) => i.productId != null)) {
      errors.items = t("form.err_items");
    }

    // Check each selected item has quantity >= 1
    items.forEach((item, index) => {
      if (item.productId != null && (!item.quantity || item.quantity < 1)) {
        errors[`item_${index}`] = t("form.err_quantity");
      }
    });

    // Check if delivery coordinates are provided
    if (!delivery.deliveryLatitude || !delivery.deliveryLongitude) {
      errors.delivery = t("form.err_location");
    }

    // Ensure delivery address text exists so drivers can display it.
    if (!delivery.deliveryAddress || !delivery.deliveryAddress.trim()) {
      errors.deliveryAddress = t("form.err_address");
    }

    // Receiver phone is required so the receiver can get EPOD code
    // (via client app and/or SMS fallback).
    if (!clientPhone || !clientPhone.trim()) {
      errors.clientPhone = t("form.err_phone_required");
    } else {
      const digits = clientPhone.replace(/\D/g, "");
      if (!/^9\d{8}$/.test(digits)) {
        errors.clientPhone = t("form.err_phone_format");
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    setError("");

    if (!merchantId) {
      setError(t("form.err_no_merchant"));
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(payload);
      setItems([emptyLine()]);
      setDelivery({});
      setClientPhone("");
      setValidationErrors({});
    } catch (err) {
      setError(friendlyError(err, t("form.create_failed")));
    } finally {
      setSubmitting(false);
    }
  }

  function updateItem(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    // Clear any validation error for this item
    if (validationErrors[`item_${idx}`] || validationErrors.items) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`item_${idx}`];
        delete newErrors.items;
        return newErrors;
      });
    }
  }

  function addItem() {
    setItems((prev) => [...prev, emptyLine()]);
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  // Check if there are no products in catalog
  if (!products || products.length === 0) {
    return (
      <div className="empty-state" style={{ margin: 24 }}>
        <i className="fa-solid fa-box-open empty-state-icon" aria-hidden="true" />
        <div style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--ink)", marginBottom: 6 }}>
          {t("form.no_products_title")}
        </div>
        <p style={{ margin: "0 auto 16px", maxWidth: 380 }}>{t("form.no_products_message")}</p>
        <button type="button" className="primary" onClick={() => (window.location.href = "/catalog")}>
          {t("form.go_to_catalog")}
        </button>
      </div>
    );
  }

  const phoneError = validationErrors.clientPhone;
  const deliveryError = validationErrors.delivery || validationErrors.deliveryAddress;
  const hasPin = delivery.deliveryLatitude && delivery.deliveryLongitude;

  return (
    <form onSubmit={submit} className="order-form">
      <div className="order-form__body">
        <Section
          step={1}
          title={t("form.products")}
          hint={t("form.products_hint")}
          error={validationErrors.items}
        >
          <div className="order-lines">
            {items.map((it, idx) => {
              const p = products.find((x) => x.id === it.productId);
              const lineTotal = p ? (Number(p.unitPrice) || 0) * (Number(it.quantity) || 0) : 0;
              const qty = Number(it.quantity) || 1;
              return (
                <div
                  key={idx}
                  className={`order-line${validationErrors[`item_${idx}`] ? " order-line--error" : ""}`}
                >
                  <img
                    className="order-line__thumb"
                    src={orderItemThumbnailSrc(p?.imageUrl)}
                    alt=""
                    width={48}
                    height={48}
                  />
                  <div className="order-line__product">
                    <select
                      aria-label={t("form.product_for_line", { n: idx + 1 })}
                      value={it.productId ?? ""}
                      onChange={(e) => {
                        const id = e.target.value ? Number(e.target.value) : null;
                        updateItem(idx, { productId: id });
                      }}
                    >
                      <option value="">{t("form.select_product")}</option>
                      {products.map((prod) => (
                        <option key={prod.id} value={prod.id}>
                          {productPickerLabel(prod)}
                        </option>
                      ))}
                    </select>
                    {p ? (
                      <div className="order-line__meta">
                        {t("form.each", { amount: formatMoney(p.unitPrice) })}
                        {productVariantSummary(p) ? ` · ${productVariantSummary(p)}` : ""}
                      </div>
                    ) : null}
                    {validationErrors[`item_${idx}`] ? (
                      <div className="field-error">{validationErrors[`item_${idx}`]}</div>
                    ) : null}
                  </div>
                  <div className="qty-stepper" aria-label={t("form.quantity")}>
                    <button
                      type="button"
                      aria-label={t("form.decrease")}
                      disabled={!it.productId || qty <= 1}
                      onClick={() => updateItem(idx, { quantity: Math.max(1, qty - 1) })}
                    >
                      <i className="fa-solid fa-minus" aria-hidden="true" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      disabled={!it.productId}
                      aria-label={t("form.quantity_for_line", { n: idx + 1 })}
                    />
                    <button
                      type="button"
                      aria-label={t("form.increase")}
                      disabled={!it.productId}
                      onClick={() => updateItem(idx, { quantity: qty + 1 })}
                    >
                      <i className="fa-solid fa-plus" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="order-line__total">
                    {p ? t("common.amount_etb", { amount: formatMoney(lineTotal) }) : "-"}
                  </div>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    aria-label={t("form.remove_line")}
                    title={t("form.remove_line")}
                  >
                    <i className="fa-regular fa-trash-can" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
          <button type="button" className="add-line-button" onClick={addItem}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> {t("form.add_product")}
          </button>
        </Section>

        <Section
          step={2}
          title={t("form.receiver")}
          hint={t("form.receiver_hint")}
          error={phoneError}
        >
          <label className="field-label" htmlFor="new-order-phone">
            {t("form.receiver_phone")}
          </label>
          <div className={`phone-field${phoneError ? " phone-field--error" : ""}`}>
            <span className="phone-field__prefix">+251</span>
            <input
              id="new-order-phone"
              type="text"
              inputMode="numeric"
              placeholder="9XXXXXXXX"
              value={clientPhone}
              onChange={(e) => {
                const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 9);
                setClientPhone(digits);
                if (validationErrors.clientPhone) {
                  setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next.clientPhone;
                    return next;
                  });
                }
              }}
              maxLength={9}
            />
          </div>
          {phoneError ? <div className="field-error">{phoneError}</div> : null}
        </Section>

        <Section
          step={3}
          title={t("form.delivery_location")}
          hint={t("form.delivery_location_hint")}
          error={deliveryError}
        >
          <DeliveryMap value={delivery} onChange={setDelivery} />
          {validationErrors.delivery ? <div className="field-error">{validationErrors.delivery}</div> : null}
          {validationErrors.deliveryAddress ? (
            <div className="field-error">{validationErrors.deliveryAddress}</div>
          ) : null}
          {hasPin ? (
            <div className="pin-summary">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                  {delivery.deliveryAddress || t("form.pinned_location")}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {delivery.deliveryLatitude.toFixed(6)}, {delivery.deliveryLongitude.toFixed(6)}
                </div>
              </div>
            </div>
          ) : null}
        </Section>

        <Section step={4} title={t("form.fee_payer")} hint={t("form.fee_payer_hint")}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              ["SENDER", t("fee.i_pay_at_pickup")],
              ["RECEIVER", t("fee.receiver_pays")],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={feePayer === value ? "primary" : undefined}
                aria-pressed={feePayer === value}
                onClick={() => setFeePayer(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </Section>

        {error ? (
          <div className="alert alert--error">
            <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
          </div>
        ) : null}
      </div>

      <div className="order-form__footer">
        <div>
          <div className="order-form__total-label">
            {totals.count === 0
              ? t("form.no_items_yet")
              : totals.count === 1
                ? t("order.one_item")
                : t("order.n_items", { n: totals.count })}
          </div>
          <div className="order-form__total">{t("common.amount_etb", { amount: formatMoney(totals.sum) })}</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {onCancel ? (
            <button type="button" onClick={onCancel} disabled={submitting}>
              {t("common.cancel")}
            </button>
          ) : null}
          <button className="primary" type="submit" disabled={submitting}>
            {submitting ? t("form.creating") : t("form.create_order")}
          </button>
        </div>
      </div>
    </form>
  );
}
